import { useState } from "react";

// What a page keeps per browser in localStorage: a tab picked, a view split,
// where a divider was left. Storage can be blocked (a private window) or hold
// what an older page wrote; either way the page works, with `initial`.

/**
 * A value kept under `key`: `read` turns what is stored into a value, or
 * undefined for what it does not take, and `write` turns a value back. Set
 * with `persist` false it changes for the page alone — a divider while it is
 * dragged, written once as it is let go rather than on every move.
 */
export function useKept<T>(
  key: string,
  initial: T,
  read: (s: string) => T | undefined,
  write: (v: T) => string,
): [T, (v: T, persist?: boolean) => void] {
  const [v, setV] = useState<T>(() => {
    const s = readKept(key);
    return s === null ? initial : (read(s) ?? initial);
  });
  const set = (next: T, persist = true) => {
    setV(next);
    if (persist) keep(key, write(next));
  };
  return [v, set];
}

/** What is kept under `key`, or null for nothing kept or storage blocked: for what is read before a page draws, as the language is. */
export function readKept(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Keep `value` under `key`; with storage blocked it lasts as long as the page. */
export function keep(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // storage blocked: it lasts as long as the page
  }
}

/** One of `choices` kept under `key` — a tab, a grouping — anything else stored read as `initial`. */
export function useKeptChoice<T extends string>(key: string, initial: T, choices: readonly T[]): [T, (v: T, persist?: boolean) => void] {
  return useKept<T>(key, initial, (s) => (choices.includes(s as T) ? (s as T) : undefined), (v) => v);
}

/** A yes or no kept under `key`, as "1" or "0". */
export function useKeptFlag(key: string, initial: boolean): [boolean, (v: boolean, persist?: boolean) => void] {
  return useKept(key, initial, (s) => (s === "1" ? true : s === "0" ? false : undefined), (v) => (v ? "1" : "0"));
}
